import { z } from 'zod';
export const SlotProgrammingOrderSchema = z.enum([
    'next',
    'shuffle',
    'ordered_shuffle',
    'alphanumeric',
    'chronological',
]);
export const SlotProgrammingFillerOrder = z.enum([
    'shuffle_prefer_short',
    'shuffle_prefer_long',
    'uniform',
]);
export const BaseSlotOrdering = z.object({
    order: SlotProgrammingOrderSchema,
    direction: z.enum(['asc', 'desc']).default('asc'),
});
export const SlotFillerTypes = z.enum([
    'head',
    'pre',
    'post',
    'tail',
    'fallback',
    'mid',
]);
export const SlotFiller = z.object({
    types: z.array(SlotFillerTypes).nonempty(),
    fillerListId: z.uuid(),
    fillerOrder: SlotProgrammingFillerOrder.optional().default('shuffle_prefer_short'),
});
export const MidRollBreakRuleSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('fixed_interval'),
        intervalMs: z.number().positive(),
    }),
    z.object({
        type: z.literal('percentage'),
        points: z.array(z.number().gt(0).lt(100)).nonempty(),
    }),
    z.object({
        type: z.literal('initial_then_interval'),
        initialDelayMs: z.number().positive(),
        intervalMs: z.number().positive(),
    }),
]);
export const MidRollConfigSchema = z
    .object({
    // V1 simple field (kept for backward compat; ignored when breakRule is set)
    intervalMs: z.number().positive().optional(),
    // V2 structured break rule. Falls back to fixed_interval(intervalMs) if absent.
    breakRule: MidRollBreakRuleSchema.optional(),
    maxBreaks: z.number().int().nonnegative(),
    minProgramDurationMs: z.number().nonnegative(),
    tailBufferMs: z.number().nonnegative().default(0),
    // Fixed duration (V1). Used when min/max are not set.
    breakDurationMs: z.number().positive().optional(),
    // Duration range (V2). System picks a random duration in [min, max] per break.
    breakDurationMinMs: z.number().positive().optional(),
    breakDurationMaxMs: z.number().positive().optional(),
    programTypes: z
        .array(z.enum(['movie', 'episode', 'track', 'music_video', 'other_video']))
        .optional(),
    // 'eager' = resolve filler at schedule time (V1 behavior)
    // 'lazy'  = emit offline placeholders, resolve at stream time
    strategy: z.enum(['eager', 'lazy']).default('eager'),
})
    .refine((data) => {
    return data.intervalMs !== undefined || data.breakRule !== undefined;
}, { message: 'Either intervalMs or breakRule must be set' })
    .refine((data) => {
    if (data.breakDurationMinMs !== undefined ||
        data.breakDurationMaxMs !== undefined) {
        return (data.breakDurationMinMs !== undefined &&
            data.breakDurationMaxMs !== undefined &&
            data.breakDurationMaxMs >= data.breakDurationMinMs);
    }
    return true;
}, {
    message: 'breakDurationMinMs and breakDurationMaxMs must both be set, and max >= min',
})
    .refine((data) => {
    return (data.breakDurationMs !== undefined ||
        (data.breakDurationMinMs !== undefined &&
            data.breakDurationMaxMs !== undefined));
}, {
    message: 'At least one of breakDurationMs or breakDurationMinMs/breakDurationMaxMs must be set',
});
export const LinkableSlot = z.object({
    id: z.uuid(),
    iterationGroup: z.uuid().optional(),
    linkMode: z.enum(['continue', 'rerun']).default('continue').optional(),
    rerunOverflow: z.enum(['flex', 'continue']).default('flex').optional(),
});
export const SlotWithFiller = z.object({
    filler: z.array(SlotFiller).optional(),
    midRoll: MidRollConfigSchema.optional(),
});
export const Slot = z.object({
    ...LinkableSlot.shape,
    ...SlotWithFiller.shape,
});
//
// Base slots
//
export const ShowProgrammingSlotSchema = z.object({
    type: z.literal('show'),
    showId: z.string(),
    seasonFilter: z.number().array().default([]).catch([]),
    seasonExcludeFilter: z.number().array().default([]).catch([]),
    ...BaseSlotOrdering.shape,
    ...Slot.shape,
});
export const FlexProgrammingSlotSchema = z.object({
    type: z.literal('flex'),
});
export const RedirectProgrammingSlotSchema = z.object({
    type: z.literal('redirect'),
    channelId: z.string(),
    channelName: z.string().optional(),
});
export const CustomShowProgrammingSlotSchema = z.object({
    type: z.literal('custom-show'),
    customShowId: z.uuid(),
    ...BaseSlotOrdering.shape,
    ...Slot.shape,
});
export const FillerProgrammingSlotSchema = z.object({
    ...Slot.shape,
    type: z.literal('filler'),
    fillerListId: z.uuid(),
    order: SlotProgrammingFillerOrder,
    durationWeighting: z.enum(['linear', 'log']),
    decayFactor: z.number().gte(0).lt(1),
    recoveryFactor: z.number().gte(0).lt(1),
});
export const SmartCollectionProgrammingSlot = z.object({
    type: z.literal('smart-collection'),
    smartCollectionId: z.uuid(),
    ...BaseSlotOrdering.shape,
    ...Slot.shape,
});
export const BaseSlotSchema = z.discriminatedUnion('type', [
    ShowProgrammingSlotSchema,
    FlexProgrammingSlotSchema,
    RedirectProgrammingSlotSchema,
    CustomShowProgrammingSlotSchema,
    FillerProgrammingSlotSchema,
    SmartCollectionProgrammingSlot,
]);
export function slotIsLinkable(slot) {
    const type = typeof slot === 'string' ? slot : slot.type;
    switch (type) {
        case 'custom-show':
        case 'show':
        case 'smart-collection':
            return true;
        case 'flex':
        case 'redirect':
        case 'filler':
            return false;
    }
}
export function slotHasFiller(slot) {
    switch (slot.type) {
        case 'custom-show':
        case 'show':
        case 'smart-collection':
            return true;
        case 'flex':
        case 'redirect':
        case 'filler':
            return false;
    }
}
//# sourceMappingURL=CommonSlots.js.map